import { ArrowLeft, ShieldAlert } from 'lucide-react'

import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { savingsPaths, useSavingsNavigate } from '@/features/savings/navigation'
import { SAVINGS_PERMISSIONS, useSavingsPermissions } from '@/features/savings/permissions'
import { DormancySweepPanel, InterestRunPanel } from '@/features/savings/run-panels'

/**
 * The period-end runs: capitalising interest, and ageing idle accounts into dormancy.
 *
 * **Refuses below `lg`.** Neither run keys an amount, which is the usual reason a screen refuses
 * here — the reason is the other one the mobile policy gives. Both reach every account at the
 * branch at once; the interest run posts a journal into the general ledger and cannot be undone
 * from any screen in this application. That is the "permanently consequential, rarely urgent"
 * case, and it belongs at a desk with the branch's figures beside it.
 *
 * Both run against the branch currently selected in the header, which is also the branch every
 * request on this screen is scoped to. There is no branch picker here on purpose: a run made
 * against a branch the operator was not looking at is exactly the mistake this screen must not
 * make possible.
 */
export function SavingsPeriodEndRunsPage() {
  const navigate = useSavingsNavigate()
  const permissions = useSavingsPermissions()
  const { selectedBranchId, currentBranch } = useBranchSelection()

  const canRunSomething = permissions.canPostInterest || permissions.canManageDormancy

  return (
    <RequiresWiderScreen
      screen="Period-end runs"
      reason="Both of these reach every account at the branch at once, and the interest run posts a journal into the general ledger that no screen here can reverse. It needs the branch's figures in front of you."
      backTo="/savings"
      backLabel="Back to savings"
    >
      <button
        type="button"
        onClick={() => navigate(savingsPaths.accounts())}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to savings accounts
      </button>

      <PageHeader
        title="Period-end runs"
        reference={currentBranch?.name ?? (selectedBranchId ? `Branch ${selectedBranchId}` : undefined)}
        description="Batch operations across one branch. They act on every account they match, and neither asks a second time."
      />

      {selectedBranchId === null ? (
        <Alert tone="warning">
          <ShieldAlert aria-hidden />
          <div className="space-y-1">
            <AlertTitle>No branch is selected</AlertTitle>
            <AlertDescription>
              Both runs are scoped to one branch. Choose one in the header first.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {!canRunSomething ? (
        <Alert tone="warning">
          <ShieldAlert aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Your role runs neither of these</AlertTitle>
            <AlertDescription>
              Capitalising interest needs{' '}
              <span className="font-mono text-xs">{SAVINGS_PERMISSIONS.interestPost}</span> and the
              dormancy sweep needs{' '}
              <span className="font-mono text-xs">{SAVINGS_PERMISSIONS.dormancyManage}</span>.
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {permissions.canPostInterest ? <InterestRunPanel branchId={selectedBranchId} /> : null}
          {permissions.canManageDormancy ? (
            <DormancySweepPanel branchId={selectedBranchId} />
          ) : null}
        </div>
      )}
    </RequiresWiderScreen>
  )
}
